import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Container, CssBaseline, Grid, Typography } from '@mui/material';
import React, { useContext } from 'react';
import { z } from 'zod';
import SnackbarContext from '../Context/SnackbarContext';
import FormInput from '../components/Form/FormInput';
import FormSelect from '../components/Form/FormSelect';
import SharedButton from '../components/Shared/SharedButton/SharedButton';
import { DebitOrderDays } from '../api/insurance-api.ts';
import { bankingSchema } from '../validation/schema';
import { useLoader } from '../Context/LoadingContext.jsx';
import { useSteps } from '../Context/StepContext.tsx';
import { useForm } from 'react-hook-form';

export const BANKING_DETAILS_KEY = "BankingDetails";

const Banking: React.FC = () => {
    const { openSnackbar } = useContext(SnackbarContext);
    const { showLoader, hideLoader } = useLoader();
    const { nextStep } = useSteps();

    type BankInputs = z.infer<typeof bankingSchema>;

    const { control, formState: { errors }, handleSubmit } = useForm({
        mode: 'onChange',
        resolver: zodResolver(bankingSchema)
    });

    const onSubmit = async (data: BankInputs) => {
        try {
            showLoader();
            localStorage.setItem(BANKING_DETAILS_KEY, JSON.stringify(data));
            openSnackbar("Banking details saved ", "success");
            nextStep();
        } catch (error) {
            openSnackbar("Failed - please try again", "error");
        }
        finally {
            hideLoader();
        }
    };

    return (
        <>
            <Container component="main" maxWidth="sm">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h3'>Banking Details</Typography>
                    <Typography variant='h5'>Please enter your banking details</Typography>
                    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                            <Typography variant='body2'>Premium Start date</Typography>
                                <FormInput
                                    name="premiumStartDate" // change
                                    control={control}
                                    label=""
                                    type="date"
                                    error={!!errors.premiumStartDate}
                                    helperText={errors.premiumStartDate?.message?.toString()}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormInput
                                    name="accountHolderName"
                                    control={control}
                                    label="Account Holder Name"
                                    error={!!errors.accountHolderName}
                                    helperText={errors.accountHolderName?.message?.toString()}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormInput
                                    name="accountType"
                                    control={control}
                                    label="Account Type"
                                    error={!!errors.accountType}
                                    helperText={errors.accountType?.message?.toString()}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormInput
                                    name="accountNumber"
                                    control={control}
                                    label="Account Number"
                                    error={!!errors.accountNumber}
                                    helperText={errors.accountNumber?.message?.toString()}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormInput
                                    name="branchCode"
                                    control={control}
                                    label="Branch Code"
                                    error={!!errors.branchCode}
                                    helperText={errors.branchCode?.message?.toString()}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormInput
                                    name="bankName"
                                    control={control}
                                    label="Bank Name"
                                    error={!!errors.bankName}
                                    helperText={errors.bankName?.message?.toString()}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormSelect
                                    name="debitOrderDay"
                                    control={control}
                                    label="Debit Order Start Date"
                                    options={[
                                        { label: "1st", value: DebitOrderDays._1 },
                                        { label: "7th", value: DebitOrderDays._7 },
                                        { label: "15th", value: DebitOrderDays._15 },
                                        { label: "20th", value: DebitOrderDays._20 },
                                        { label: "25th", value: DebitOrderDays._25 },
                                        { label: "26th", value: DebitOrderDays._26 },
                                        { label: "27th", value: DebitOrderDays._27 },
                                        { label: "30th", value: DebitOrderDays._30 }
                                    ]}
                                    error={Boolean(errors.debitOrderDay)}
                                    helperText={errors.debitOrderDay?.message?.toString() || "Debit Order start day"}
                                />
                            </Grid>
                        </Grid>
                        <Box display="flex" justifyContent="center" alignItems="center" marginTop={'20px'} marginBottom={"40px"}>
                            <SharedButton type={'submit'} text={`Submit`}></SharedButton>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default Banking;
